import type { TrackRecord } from "../types";
import { setDetailTitle } from "../meta";
import { escapeHtml } from "../util/html";
import { resolveAudioUrl } from "../util/audio";
import { deferForFastPaint } from "../util/defer";
import { fetchJson } from "../util/fetch-json";

/** 仅含「本地已有音频文件」的曲目，由 npm run pack-local-tracks 生成 */
const TRACKS_DATA_URL = "/data/tracks-local.json";
/** 曲目拓展讲解（content/music/data/ai-explains.json，经 assemble 到 public/data） */
const AI_EXPLAINS_URL = "/data/ai-explains.json";

type AiExplainRow = { trackId?: number; explain?: string };

/** 去掉历史数据里的「云侣音乐」AI 开场白 */
function sanitizeAiExplainText(raw: string): string {
  let s = raw.replace(/\r\n/g, "\n");
  s = s.replace(/^欢迎来到云侣音乐的\s*AI\s*音乐讲解[。.！]?\s*/u, "");
  s = s.replace(/^欢迎来到云侣[^。\n]{0,48}[。.！]?\s*/u, "");
  s = s.replace(/云侣音乐/gu, "尚诺艾");
  return s.trim();
}

export function renderMusic(): string {
  return `
    <div id="music-root" class="music-page">
      <p class="section-sub" style="margin-top:0">正在加载曲库…</p>
    </div>
  `;
}

/** 列表页多播放器互斥：开始播放一首时暂停同页其它曲目 */
function wireExclusiveMusicGridPlayback(grid: HTMLElement): void {
  const audios = [...grid.querySelectorAll<HTMLAudioElement>("audio")];
  for (const current of audios) {
    current.addEventListener("play", () => {
      for (const other of audios) {
        if (other !== current) {
          other.pause();
        }
      }
    });
  }
}

function normalizeMusicEra(era: string | undefined): string {
  const e = (era || "").trim();
  if (e === "古典主义/浪漫主义") return "古典主义";
  return e;
}

function eraFilters(eras: string[], active: string): string {
  const chips = eras
    .map(
      (era) =>
        `<button type="button" class="filter-chip ${active === era ? "active" : ""}" data-era="${escapeHtml(era)}">${escapeHtml(era)}</button>`
    )
    .join("");
  return `<div class="filter-row" role="tablist" aria-label="按时期筛选">${chips}</div>`;
}

function trackCard(t: TrackRecord): string {
  const meta = [t.composer, t.era, t.year != null ? String(t.year) : ""].filter(Boolean).join(" · ");
  const src = t.audioFile ? escapeHtml(resolveAudioUrl(t.audioFile)) : "";
  /** 列表页不要用 preload=metadata：会同时对几十条音频打 Range 请求，dev 与本机 I/O 会明显变慢 */
  const player = src
    ? `<div class="music-card-play"><audio controls preload="none" src="${src}"></audio></div>`
    : "";
  const intro = escapeHtml(t.introBlurb ?? "");
  return `
    <div class="music-card-wrap">
      <a class="music-card music-card-main" href="/music?track=${t.id}">
        <h3>${escapeHtml(t.title)}</h3>
        <div class="card-meta">${escapeHtml(meta)}</div>
        ${intro ? `<p class="music-card-intro">${intro}</p>` : ""}
        ${t.tags?.length ? `<div class="tag-row">${t.tags.slice(0, 4).map((x) => `<span class="tag">${escapeHtml(x)}</span>`).join("")}</div>` : ""}
      </a>
      ${player}
    </div>
  `;
}

function detailView(t: TrackRecord, aiExplain?: string): string {
  const audio = t.audioFile ? resolveAudioUrl(t.audioFile) : "";
  const tags =
    t.tags?.map((x) => `<span class="tag">${escapeHtml(x)}</span>`).join("") ?? "";
  const blurb = t.introBlurb ? `<p class="detail-lead">${escapeHtml(t.introBlurb)}</p>` : "";
  const explainTrim = aiExplain ? sanitizeAiExplainText(aiExplain) : "";
  const aiBlock = explainTrim
    ? `<h2 class="detail-heading">拓展解读</h2>
        <div class="music-ai-inline-text">${escapeHtml(explainTrim)}</div>`
    : "";
  return `
    <div class="detail-layout">
      <a href="/music" class="text-link back-link">← 返回曲库</a>
      <article class="detail-article">
        <h1 class="section-title" style="margin-top:0.5rem">${escapeHtml(t.title)}</h1>
        <p class="detail-sub">${escapeHtml(t.titleEn)} · ${escapeHtml(t.composer)}${t.year != null ? ` · ${t.year}` : ""}</p>
        ${blurb}
        <div class="tag-row">${tags}</div>
        ${
          audio
            ? `<div class="audio-block">
                <audio controls preload="metadata" src="${escapeHtml(audio)}"></audio>
                <p class="viz-entry">
                  <a class="viz-entry-link" href="/visualizer?track=${t.id}">沉浸视听 →</a>
                  <span class="viz-entry-hint">Three.js 实时 3D 频谱</span>
                </p>
              </div>`
            : `<p class="section-sub">暂无音频路径</p>`
        }
        <h2 class="detail-heading">作品与背景</h2>
        <p class="detail-body">${escapeHtml(t.story ?? "暂无讲解文案。")}</p>
        ${aiBlock}
        <p class="section-sub">曲目 ID：<code>${t.id}</code></p>
      </article>
    </div>
  `;
}

function listView(tracks: TrackRecord[], era: string): string {
  const normalized = tracks.map((t) => ({ ...t, era: normalizeMusicEra(t.era) }));
  const eras = ["全部", ...Array.from(new Set(normalized.map((x) => x.era).filter(Boolean))).sort()];
  const filtered = era === "全部" ? normalized : normalized.filter((x) => x.era === era);
  return `
    <h1 class="section-title" style="margin-top:0">名曲馆</h1>
    ${eraFilters(eras, era)}
    <div class="music-grid" id="music-grid">
      ${filtered.map(trackCard).join("")}
    </div>
    ${filtered.length === 0 ? '<p class="section-sub">该时期暂无曲目。</p>' : ""}
  `;
}

type TracksPayload = { tracks: TrackRecord[]; count?: number };

/** 带 `?track=` 的详情直链尽快出内容；列表页则首帧后再拉曲库 JSON */
export function hydrateMusic(container: HTMLElement): void {
  const params = new URLSearchParams(window.location.search);
  const trackIdRaw = params.get("track");
  const trackId = trackIdRaw != null ? Number(trackIdRaw) : NaN;
  const detailNow = Number.isFinite(trackId);

  const run = () => void hydrateMusicAsync(container);
  if (detailNow) run();
  else deferForFastPaint(run);
}

function normalizeAiExplainRows(rows: unknown): AiExplainRow[] {
  if (Array.isArray(rows)) return rows as AiExplainRow[];
  if (rows != null && typeof rows === "object") {
    const o = rows as { explains?: unknown };
    if (Array.isArray(o.explains)) return o.explains as AiExplainRow[];
  }
  return [];
}

function explainForTrack(rows: unknown, trackId: number): string | undefined {
  for (const x of normalizeAiExplainRows(rows)) {
    if (x != null && typeof x === "object") {
      const o = x as AiExplainRow;
      if (o.trackId === trackId && typeof o.explain === "string") {
        return sanitizeAiExplainText(o.explain);
      }
    }
  }
  return undefined;
}

async function hydrateMusicAsync(container: HTMLElement): Promise<void> {
  const root = container.querySelector("#music-root");
  if (!root) return;

  const params = new URLSearchParams(window.location.search);
  const trackIdRaw = params.get("track");
  const trackId = trackIdRaw != null ? Number(trackIdRaw) : NaN;
  const detailNow = Number.isFinite(trackId);

  try {
    let aiRows: unknown = null;
    const data = detailNow
      ? await (async () => {
          const [tracksPayload, aiPayload] = await Promise.all([
            fetchJson<TracksPayload>(TRACKS_DATA_URL, "曲库"),
            fetchJson<unknown>(AI_EXPLAINS_URL, "拓展解读").catch(() => null),
          ]);
          aiRows = aiPayload;
          return tracksPayload;
        })()
      : await fetchJson<TracksPayload>(TRACKS_DATA_URL, "曲库");
    if (!root.isConnected) return;
    const tracks = data.tracks ?? [];

    if (Number.isFinite(trackId)) {
      const t = tracks.find((x) => x.id === trackId);
      if (t) {
        if (!root.isConnected) return;
        const aiExplain = explainForTrack(aiRows, t.id);
        if (!root.isConnected) return;
        root.innerHTML = detailView(t, aiExplain);
        setDetailTitle(`${t.title} · 名曲馆`);
        return;
      }
      if (!root.isConnected) return;
      root.innerHTML = `<p class="section-sub">未找到曲目 ID ${escapeHtml(String(trackIdRaw))}（当前收录 ${tracks.length} 首已确认本地文件的曲目）。<a href="/music">返回曲库</a></p>`;
      return;
    }

    let activeEra = sessionStorage.getItem("sanoi-music-era") || "全部";
    if (activeEra === "古典主义/浪漫主义") activeEra = "古典主义";
    if (activeEra !== "全部" && !tracks.some((x) => normalizeMusicEra(x.era) === activeEra)) activeEra = "全部";

    const renderList = (era: string) => {
      if (!root.isConnected) return;
      root.innerHTML = listView(tracks, era);
      sessionStorage.setItem("sanoi-music-era", era);
      root.querySelectorAll<HTMLButtonElement>(".filter-chip").forEach((btn) => {
        btn.addEventListener("click", () => {
          const next = btn.getAttribute("data-era") || "全部";
          renderList(next);
        });
      });
      const grid = root.querySelector<HTMLElement>("#music-grid");
      if (grid) wireExclusiveMusicGridPlayback(grid);
    };
    renderList(activeEra);
  } catch (e) {
    if (root.isConnected) {
      const msg = e instanceof Error ? e.message : "曲库暂时无法加载";
      root.innerHTML = `<p class="section-sub">${escapeHtml(msg)}。若为本机开发，请在项目根目录执行 <code>npm run content:assemble</code> 后刷新。</p>`;
    }
  }
}
